// The last setup a player started with, per browser (spec §11). Stored as
// the share code plus the human seat's assists and the AI profile, so a
// stale or hand-edited entry goes through the same validation as a pasted
// code. Every storage access is wrapped: private mode, blocked storage or a
// full quota just means "no remembered setup".

import type { AiProfile, Assists, SeatAlignment } from '@aod/protocol';
import { type SetupDraft, draftFromRuleset } from './setupModel.js';
import { decodeShareCode, encodeShareCode } from './shareCode.js';

export const LAST_SETUP_KEY = 'aod.setup.last.v1';

interface Stored {
  readonly code: string;
  readonly assists?: Partial<Assists>;
  readonly aiProfile?: string;
  readonly alignment?: string;
}

const ASSIST_VALUES: { readonly [K in keyof Assists]: readonly Assists[K][] } = {
  handicap: ['none', 'easy', 'very_easy'],
  autoManageCities: ['off', 'suggest', 'auto'],
  hints: ['off', 'smart', 'always'],
  revealMap: [false, true],
  pauseOnDanger: ['off', 'major', 'all'],
  uiMode: ['icons', 'standard', 'expert'],
  speedCap: [1, 2, 4],
};

export const AI_PROFILES: readonly AiProfile[] = ['gentle', 'easy', 'normal', 'hard', 'brutal'];

const SEAT_ALIGNMENTS: readonly SeatAlignment[] = ['light', 'dark'];

/** Keep only well-formed assist values; missing ones fall back to `base`. */
function sanitiseAssists(raw: Partial<Assists> | undefined, base: Assists): Assists {
  const out: Record<string, unknown> = { ...base };
  if (raw && typeof raw === 'object') {
    for (const key of Object.keys(ASSIST_VALUES) as (keyof Assists)[]) {
      const allowed = ASSIST_VALUES[key] as readonly unknown[];
      if (allowed.includes(raw[key])) out[key] = raw[key];
    }
  }
  return out as unknown as Assists;
}

export function serialiseSetup(d: SetupDraft): string {
  const stored: Stored = {
    code: encodeShareCode(d.ruleset, d.basedOn),
    assists: d.humanAssists,
    aiProfile: d.aiProfile,
    ...(d.alignment !== null ? { alignment: d.alignment } : {}),
  };
  return JSON.stringify(stored);
}

export function parseSetup(raw: string | null): SetupDraft | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as Stored;
    if (typeof stored?.code !== 'string') return null;
    const decoded = decodeShareCode(stored.code);
    if (!decoded.ok) return null;
    const { ruleset, basedOn } = decoded.value;
    const base = draftFromRuleset(ruleset, basedOn);
    return draftFromRuleset(ruleset, basedOn, {
      humanAssists: sanitiseAssists(stored.assists, base.humanAssists),
      aiProfile: AI_PROFILES.includes(stored.aiProfile as AiProfile)
        ? (stored.aiProfile as AiProfile)
        : base.aiProfile,
      alignment: SEAT_ALIGNMENTS.includes(stored.alignment as SeatAlignment)
        ? (stored.alignment as SeatAlignment)
        : null,
    });
  } catch {
    return null;
  }
}

export function loadLastSetup(): SetupDraft | null {
  try {
    return parseSetup(localStorage.getItem(LAST_SETUP_KEY));
  } catch {
    return null;
  }
}

export function saveLastSetup(d: SetupDraft): void {
  try {
    localStorage.setItem(LAST_SETUP_KEY, serialiseSetup(d));
  } catch {
    /* storage unavailable — the setup just isn't remembered */
  }
}
